import { useState } from "react";
import { GoldService } from "@/services/APIs";
import { useToast } from "@/hooks/use-toast";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/common/components/ui/dialog";
import { Button } from "@/common/components/ui/button";
import { Label } from "@/common/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/common/components/ui/select";
import { Upload, AlertCircle, FileSpreadsheet } from "lucide-react";

interface ImportDeliveryMasterModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const ImportDeliveryMasterModal = ({
  isOpen,
  onClose,
}: ImportDeliveryMasterModalProps) => {
  const [file, setFile] = useState<File | null>(null);
  const [source, setSource] = useState("bvc");
  const [isUploading, setIsUploading] = useState(false);
  const { toast } = useToast();

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.[0]) {
      setFile(e.target.files[0]);
    }
  };

  const handleImport = async () => {
    if (!file) {
      toast({
        variant: "destructive",
        title: "Validation Error",
        description: "Please select a file to upload.",
      });
      return;
    }

    const formData = new FormData();
    formData.append("source", source);
    formData.append("file", file);

    setIsUploading(true);
    try {
      const response = await GoldService.importDeliveryMasters(formData);
      if (response?.success) {
        toast({
          title: "Import Successful",
          description: response.message ?? "Records imported successfully.",
        });
        onClose();
      } else {
        throw new Error(response.message ?? "Import failed.");
      }
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Import Error",
        description:
          error.message ??
          "Failed to import file. Please check format and try again.",
      });
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-white sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5 text-primary" />
            Bulk Import Delivery Masters
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-6 py-4">
          <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50/50 p-6 text-center">
            <div className="flex flex-col items-center gap-2">
              <FileSpreadsheet className="h-10 w-10 text-gray-400" />
              <div className="text-sm font-medium text-gray-700">
                {file ? file.name : "Select CSV/Excel File"}
              </div>

              <label className="mt-4 cursor-pointer rounded-md bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90">
                Choose File
                <input
                  type="file"
                  className="hidden"
                  accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                  onChange={handleFileChange}
                />
              </label>
            </div>
          </div>

          <div className="flex flex-col gap-2 rounded-lg border border-gray-200 bg-gray-50/50 p-4">
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">
              Courier Partner Source *
            </Label>
            <Select value={source} onValueChange={setSource}>
              <SelectTrigger className="w-full bg-white">
                <SelectValue placeholder="Select Partner" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="bvc">BVC</SelectItem>
                <SelectItem value="bluedart">Blue Dart</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-start gap-2 rounded-lg bg-amber-50 p-3 text-sm text-amber-800">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />
            <p>
              Ensure the file clearly matches the required columns for
              respective partners.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={isUploading}>
            Cancel
          </Button>
          <Button onClick={handleImport} disabled={!file || isUploading}>
            {isUploading ? "Importing..." : "Upload & Import"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ImportDeliveryMasterModal;
